import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Save, ChevronRight } from "lucide-react";
import { DEFAULT_CHROME, mergeChrome } from "@/lib/chromeContent";
import { ImageField } from "./ImageField";

const L = ({ children }) => <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">{children}</div>;
const F = (p) => <Input {...p} className="bg-black/40 border-white/10 text-sm" />;

export const ChromeEditor = () => {
  const [cfg, setCfg] = useState(DEFAULT_CHROME);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/content/chrome").then(({ data }) => setCfg(mergeChrome(data))).catch(() => {}); }, []);

  const setHeader = (patch) => setCfg((c) => ({ ...c, header: { ...c.header, ...patch } }));
  const setFooter = (patch) => setCfg((c) => ({ ...c, footer: { ...c.footer, ...patch } }));
  const menu = cfg.header.menu || [];
  const setMenu = (m) => setHeader({ menu: m });
  const cols = cfg.footer.columns || [];
  const setCols = (x) => setFooter({ columns: x });

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/content/chrome", { header: cfg.header, footer: cfg.footer }); toast.success("Header & footer saved — live now"); }
    catch { toast.error("Save failed"); }
    setSaving(false);
  };

  return (
    <div data-testid="chrome-editor" className="max-w-3xl space-y-8 pb-28">
      <div>
        <h1 className="font-serif text-4xl text-white">Header &amp; Footer</h1>
        <p className="text-slate-400 text-sm mt-2">Edit the logo, navigation (with dropdowns) and footer links. Changes go live immediately across the whole site.</p>
      </div>

      {/* ───────── HEADER ───────── */}
      <section className="glass rounded-2xl p-6 space-y-5">
        <h2 className="text-amber-200 font-mono uppercase tracking-widest text-xs">Header</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><L>Logo text — bold</L><F value={cfg.header.logo_text_1 || ""} onChange={(e) => setHeader({ logo_text_1: e.target.value })} data-testid="hdr-logo1" /></div>
          <div><L>Logo text — light</L><F value={cfg.header.logo_text_2 || ""} onChange={(e) => setHeader({ logo_text_2: e.target.value })} data-testid="hdr-logo2" /></div>
        </div>
        <div><L>Logo image (optional — replaces text when set)</L><ImageField value={cfg.header.logo_image} onChange={(v) => setHeader({ logo_image: v })} testid="hdr-logo-img" /></div>

        <div>
          <L>Navigation — parent items &amp; dropdown children</L>
          <div className="space-y-3">
            {menu.map((item, i) => (
              <div key={i} className="rounded-xl border border-white/10 p-3 bg-black/20" data-testid={`menu-item-${i}`}>
                <div className="flex gap-2 items-center">
                  <F placeholder="Label" value={item.label || ""} onChange={(e) => setMenu(menu.map((m, j) => (j === i ? { ...m, label: e.target.value } : m)))} data-testid={`menu-label-${i}`} />
                  <F placeholder="/link" value={item.to || ""} onChange={(e) => setMenu(menu.map((m, j) => (j === i ? { ...m, to: e.target.value } : m)))} data-testid={`menu-to-${i}`} />
                  <Button size="icon" variant="ghost" onClick={() => setMenu(menu.filter((_, j) => j !== i))} className="shrink-0 text-rose-400 hover:text-rose-300" data-testid={`menu-del-${i}`}><Trash2 size={14} /></Button>
                </div>
                <div className="mt-2 ml-3 pl-3 border-l border-white/10 space-y-2">
                  {(item.children || []).map((ch, k) => (
                    <div key={k} className="flex gap-2 items-center" data-testid={`menu-${i}-child-${k}`}>
                      <ChevronRight size={12} className="text-slate-600 shrink-0" />
                      <F placeholder="Dropdown label" value={ch.label || ""} onChange={(e) => setMenu(menu.map((m, j) => (j === i ? { ...m, children: (m.children || []).map((c, x) => (x === k ? { ...c, label: e.target.value } : c)) } : m)))} />
                      <F placeholder="/link" value={ch.to || ""} onChange={(e) => setMenu(menu.map((m, j) => (j === i ? { ...m, children: (m.children || []).map((c, x) => (x === k ? { ...c, to: e.target.value } : c)) } : m)))} />
                      <Button size="icon" variant="ghost" onClick={() => setMenu(menu.map((m, j) => (j === i ? { ...m, children: (m.children || []).filter((_, x) => x !== k) } : m)))} className="shrink-0 text-rose-400 hover:text-rose-300"><Trash2 size={13} /></Button>
                    </div>
                  ))}
                  <Button size="sm" variant="ghost" onClick={() => setMenu(menu.map((m, j) => (j === i ? { ...m, children: [...(m.children || []), { label: "", to: "" }] } : m)))} className="btn-ghost rounded-lg text-[11px]" data-testid={`menu-${i}-add-child`}><Plus size={12} /> Add dropdown item</Button>
                </div>
              </div>
            ))}
            <Button size="sm" variant="ghost" onClick={() => setMenu([...menu, { label: "", to: "", children: [] }])} className="btn-ghost rounded-lg text-xs" data-testid="menu-add"><Plus size={13} /> Add menu item</Button>
          </div>
        </div>
      </section>

      {/* ───────── FOOTER ───────── */}
      <section className="glass rounded-2xl p-6 space-y-5">
        <h2 className="text-amber-200 font-mono uppercase tracking-widest text-xs">Footer</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><L>Logo text — bold</L><F value={cfg.footer.logo_text_1 || ""} onChange={(e) => setFooter({ logo_text_1: e.target.value })} data-testid="ftr-logo1" /></div>
          <div><L>Logo text — light</L><F value={cfg.footer.logo_text_2 || ""} onChange={(e) => setFooter({ logo_text_2: e.target.value })} data-testid="ftr-logo2" /></div>
        </div>
        <div><L>Footer logo image (optional)</L><ImageField value={cfg.footer.logo_image} onChange={(v) => setFooter({ logo_image: v })} testid="ftr-logo-img" /></div>
        <div><L>Tagline</L><F value={cfg.footer.tagline || ""} onChange={(e) => setFooter({ tagline: e.target.value })} data-testid="ftr-tagline" /></div>
        <div><L>Description</L><Textarea value={cfg.footer.description || ""} onChange={(e) => setFooter({ description: e.target.value })} className="bg-black/40 border-white/10 text-sm" rows={2} /></div>
        <div><L>Disclaimer</L><Textarea value={cfg.footer.disclaimer || ""} onChange={(e) => setFooter({ disclaimer: e.target.value })} className="bg-black/40 border-white/10 text-sm" rows={2} /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><L>Bottom bar — left</L><F value={cfg.footer.bottom_left || ""} onChange={(e) => setFooter({ bottom_left: e.target.value })} /></div>
          <div><L>Bottom bar — right</L><F value={cfg.footer.bottom_right || ""} onChange={(e) => setFooter({ bottom_right: e.target.value })} /></div>
        </div>
        <div>
          <L>Footer link columns</L>
          <div className="space-y-3">
            {cols.map((col, i) => (
              <div key={i} className="rounded-xl border border-white/10 p-3 bg-black/20" data-testid={`ftr-col-${i}`}>
                <div className="flex gap-2 items-center mb-2">
                  <F placeholder="Column heading" value={col.heading || ""} onChange={(e) => setCols(cols.map((c, j) => (j === i ? { ...c, heading: e.target.value } : c)))} data-testid={`ftr-col-head-${i}`} />
                  <Button size="icon" variant="ghost" onClick={() => setCols(cols.filter((_, j) => j !== i))} className="shrink-0 text-rose-400 hover:text-rose-300"><Trash2 size={14} /></Button>
                </div>
                <div className="ml-3 pl-3 border-l border-white/10 space-y-2">
                  {(col.links || []).map((lnk, k) => (
                    <div key={k} className="flex gap-2 items-center">
                      <F placeholder="Label" value={lnk.label || ""} onChange={(e) => setCols(cols.map((c, j) => (j === i ? { ...c, links: (c.links || []).map((l, x) => (x === k ? { ...l, label: e.target.value } : l)) } : c)))} />
                      <F placeholder="/link" value={lnk.to || ""} onChange={(e) => setCols(cols.map((c, j) => (j === i ? { ...c, links: (c.links || []).map((l, x) => (x === k ? { ...l, to: e.target.value } : l)) } : c)))} />
                      <Button size="icon" variant="ghost" onClick={() => setCols(cols.map((c, j) => (j === i ? { ...c, links: (c.links || []).filter((_, x) => x !== k) } : c)))} className="shrink-0 text-rose-400 hover:text-rose-300"><Trash2 size={13} /></Button>
                    </div>
                  ))}
                  <Button size="sm" variant="ghost" onClick={() => setCols(cols.map((c, j) => (j === i ? { ...c, links: [...(c.links || []), { label: "", to: "" }] } : c)))} className="btn-ghost rounded-lg text-[11px]"><Plus size={12} /> Add link</Button>
                </div>
              </div>
            ))}
            <Button size="sm" variant="ghost" onClick={() => setCols([...cols, { heading: "", links: [] }])} className="btn-ghost rounded-lg text-xs" data-testid="ftr-col-add"><Plus size={13} /> Add column</Button>
          </div>
        </div>
      </section>

      <div className="sticky bottom-4 flex justify-end">
        <Button onClick={save} disabled={saving} className="btn-primary rounded-xl px-6" data-testid="chrome-save"><Save size={15} /> {saving ? "Saving…" : "Save Header & Footer"}</Button>
      </div>
    </div>
  );
};
